/* eslint-disable react/no-danger */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { graphql } from 'gatsby';
import { useWindowSize } from 'hooks';
import DOMPurify from 'isomorphic-dompurify';
import { LocalContext } from 'context';
import { getAltTagFromFileName, setFiltersInitialState } from 'utils';
import {
  ProductFilters,
  DismissDialog,
  SEO,
  Cards,
  Dialog,
  MobileSearchAndFilters
} from 'components';
import { Grid, Hero } from 'components/ProductCollectionPage/Shared';

function Doors({ data }) {
  const { doors, heroData } = data;
  const { heroTitle, heroDescription, heroImage, gradientPercentage, heroImageOffset, filter } =
    heroData.frontmatter;
  const { handleDialog } = useContext(LocalContext);
  const allProducts = doors.nodes.map((node) => ({
    ...node,
    // We've to wrap some of the below string values with arrays, so that filtering
    // works. On the Dynamic Events site we did these string-to-array transformations on the
    // CMS side of things, but that made the CMS UI a bit more confusing that it
    // had to be. For Connolly Stairs, we're doing the transformations here client-side.
    // Same result, just keeps the object types exactly as they should be in the CMS.
    finish: node.finish ? [node.finish] : [],
    glazing: node.glazing ? [node.glazing] : [],
    numberOfPanels: [node.numberOfPanels.toString()],
    numberOfGlassPanes: [node.numberOfGlassPanes.toString()],
    sustainabilityCert: node.sustainabilityCert ? [node.sustainabilityCert] : [],
    fireResistance: node.fireResistance ? [node.fireResistance] : []
  }));

  const [products, setProducts] = useState(allProducts);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductFiltersDialog, setShowProductFiltersDialog] = useState(false);

  const productFiltersDialogRef = useRef();

  const { windowWidth } = useWindowSize();

  const filterCategories = allProducts.map((node) => ({
    productCollection: node.productCollection,
    finish: node.finish,
    glazing: node.glazing,
    numberOfPanels: node.numberOfPanels,
    numberOfGlassPanes: node.numberOfGlassPanes,
    sustainabilityCert: node.sustainabilityCert,
    fireResistance: node.fireResistance
  }));

  const [filterValues, setFilterValues] = useState(setFiltersInitialState(filterCategories));

  const filterProducts = (card) => {
    const {
      finish,
      productCollection,
      glazing,
      numberOfPanels,
      numberOfGlassPanes,
      sustainabilityCert,
      fireResistance
    } = card;

    const getCurrentlySelectedFiltersInThisCategory = (filters) =>
      Object.entries(filters)
        .filter((_filter) => _filter[1])
        .map((_filter) => _filter[0]);

    const condition1 =
      Object.values(filterValues.finish).every((boolean) => !boolean) ||
      finish.some((supportType) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.finish).includes(supportType)
      );

    const condition2 =
      Object.values(filterValues.productCollection).every((boolean) => !boolean) ||
      productCollection.some((location) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.productCollection).includes(location)
      );

    const condition4 =
      Object.values(filterValues.glazing).every((boolean) => !boolean) ||
      glazing.some((_glazing) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.glazing).includes(_glazing)
      );

    const condition5 =
      Object.values(filterValues.numberOfPanels).every((boolean) => !boolean) ||
      numberOfPanels.some((number) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.numberOfPanels).includes(number)
      );

    const condition6 =
      Object.values(filterValues.numberOfGlassPanes).every((boolean) => !boolean) ||
      numberOfGlassPanes.some((theme) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.numberOfGlassPanes).includes(theme)
      );

    const condition7 =
      Object.values(filterValues.sustainabilityCert).every((boolean) => !boolean) ||
      sustainabilityCert.some((cert) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.sustainabilityCert).includes(cert)
      );

    const condition8 =
      Object.values(filterValues.fireResistance).every((boolean) => !boolean) ||
      fireResistance.some((cert) =>
        getCurrentlySelectedFiltersInThisCategory(filterValues.fireResistance).includes(cert)
      );

    if (
      condition1 &&
      condition2 &&
      condition4 &&
      condition5 &&
      condition6 &&
      condition7 &&
      condition8
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const filteredProducts = allProducts.filter(filterProducts);

    if (searchQuery === '') {
      setProducts(filteredProducts.length ? filteredProducts : []);
    } else if (searchQuery !== '') {
      const searchFilteredCards = filteredProducts.filter(
        (card) =>
          card.finish.some((finish) =>
            finish.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.productCollection.some((productCategory) =>
            productCategory.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.glazing.some((glazing) =>
            glazing.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.numberOfPanels.some((numberOfPanels) =>
            numberOfPanels.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.numberOfGlassPanes.some((numberOfGlassPanes) =>
            numberOfGlassPanes.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.sustainabilityCert.some((cert) =>
            cert.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.fireResistance.some((fireResistance) =>
            fireResistance.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          card.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setProducts(searchFilteredCards);
    }
  }, [filterValues, searchQuery]);

  // useEffect(() => {
  //   const _checkedFilterValues = Object.entries(filterValues).reduce(
  //     (acc, [filterTitle, nestedObj]) => {
  //       acc[filterTitle] = Object.entries(nestedObj)
  //         .slice(1)
  //         .reduce((_acc, [prop, value]) => {
  //           if (value === true) {
  //             _acc[prop] = value;
  //           }
  //           return _acc;
  //         }, {});
  //       return acc;
  //     },
  //     {}
  //   );
  //   const queryString = qs.stringify(_checkedFilterValues);
  //   if (queryString) {
  //     window.history.replaceState(null, null, `?${queryString}`);
  //   }
  // }, [filterValues]);

  const handleSearchInput = ({ target }) => {
    const { value } = target;
    setSearchQuery(value);
  };

  const handleFilterClick = ({ e, filterCategory }) => {
    setFilterValues((currentValues) => ({
      ...currentValues,
      [filterCategory]: {
        ...currentValues[filterCategory],
        [e.target.name]: e.target.checked
      }
    }));

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // useEffect(() => {
  //   if (Object.keys(parameters).length > 0) {
  //     Object.keys(parameters).forEach((parameter) => {
  //       if (filterValues[parameter][parameters[parameter]] !== undefined) {
  //         const allFilter = Object.keys(filterValues[parameter])[0];
  //         const selectedFilter = parameters[parameter];
  //         const otherFilters = Object.keys(filterValues[parameter])
  //           .slice(1)
  //           .reduce((acc, filter) => {
  //             acc[filter] = false;
  //             return acc;
  //           }, {});

  //         setFilterValues((currentValues) => ({
  //           ...currentValues,
  //           [parameter]: {
  //             [allFilter]: false,
  //             ...otherFilters,
  //             [selectedFilter]: true
  //           }
  //         }));
  //       }
  //     });
  //   }
  // }, [parameters]);

  const handleProductFiltersDialog = ({ animation, callback }) => {
    handleDialog({
      dialogRef: productFiltersDialogRef,
      animation,
      stateHandler: () => setShowProductFiltersDialog(false),
      callback
    });
  };

  return (
    <>
      <Hero
        heroImage={heroImage}
        gradientPercentage={gradientPercentage}
        heroImageOffset={heroImageOffset}
        filter={filter}
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Doors"
        breadcrumb="Doors">
        <h1
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroTitle) }}
          style={{ fontSize: '2.5rem' }}
        />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <Grid>
        {windowWidth < 768 ? (
          <>
            <MobileSearchAndFilters
              searchQuery={searchQuery}
              handleSearchInput={handleSearchInput}
              setShowProductFiltersDialog={setShowProductFiltersDialog}
            />
            <Dialog
              ref={productFiltersDialogRef}
              isVisible={showProductFiltersDialog}
              onDismiss={() => handleProductFiltersDialog({ animation: 'dismiss' })}
              contentStyle={{ maxWidth: '340px', height: '100%' }}>
              <DismissDialog onClick={() => handleProductFiltersDialog({ animation: 'dismiss' })} />
              <ProductFilters
                openedInDialog
                filterCategories={filterCategories}
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                handleFilterClick={handleFilterClick}
                handleSearchInput={handleSearchInput}
                searchQuery={searchQuery}
                handleProductFiltersDialog={handleProductFiltersDialog}
              />
            </Dialog>
          </>
        ) : (
          <ProductFilters
            filterCategories={filterCategories}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            handleFilterClick={handleFilterClick}
            handleSearchInput={handleSearchInput}
            searchQuery={searchQuery}
          />
        )}
        <Cards
          products={products}
          imageWidth={340}
          imageHeight={380}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          productsPerPage={windowWidth < 1200 ? 18 : 15}
        />
      </Grid>
    </>
  );
}

export const query = graphql`
  query {
    heroData: markdownRemark(frontmatter: { productCollectionName: { eq: "Doors" } }) {
      id
      frontmatter {
        productCollectionName
        heroTitle
        heroDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
        gradientPercentage
        heroImageOffset
        filter
      }
    }
    doors: allSanityDoor {
      totalCount
      nodes {
        id
        title
        finish
        glazing
        numberOfPanels
        numberOfGlassPanes
        productCollection
        description
        sustainabilityCert
        fireResistance
        images {
          _key
          hotspot {
            height
            width
            x
            y
          }
          crop {
            top
            bottom
            left
            right
          }
          asset {
            _id
            originalFilename
            url
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
        productVariants {
          variantGroupID
          default {
            productId
            dimensions
          }
          additional {
            productId
            dimensions
          }
        }
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="Quality Interior & Exterior Doors Ireland | Connolly Stairs & Doors"
      description="Explore our extensive range of interior and exterior doors designed for durability, style, and elegance. Connolly Stairs & Doors has your door needs covered.">
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Stairs Ireland", "item": "https://www.connollystairs.ie/" },
          { "@type": "ListItem", "position": 2, "name": "Doors", "item": "https://www.connollystairs.ie/doors/" }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default Doors;
